import { CommonModule, CurrencyPipe } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ApplicationService } from 'app/services/application.service';
import { PdfService } from 'app/services/pdf.service';
import { ToastrService } from 'ngx-toastr';

@Component({
    selector: 'app-application-details',
    standalone: true,
    imports: [CommonModule, TranslateModule, MatIconModule],
    providers: [CurrencyPipe],
    templateUrl: './application-details.component.html',
    styleUrl: './application-details.component.scss',
})
export class ApplicationDetailsComponent {
    businessTypeOptions = [
        {
            value: 'SP',
            label: 'COMMON.SOLE_PROPRIETORSHIP',
        },
        {
            value: 'PN',
            label: 'COMMON.PARTNERSHIP',
        },
        {
            value: 'LLP',
            label: 'COMMON.LIMITED_LIABILITY',
        },
        {
            value: 'SDNBHD',
            label: 'COMMON.SDNBHD',
        },
        {
            value: 'PROFBODIES',
            label: 'COMMON.PROFESSIONAL_SERVICE_PROVIDER',
        },
        {
            value: 'IND',
            label: 'COMMON.INDIVIDUAL',
        },
    ];
    businessIndustryOptions = [
        {
            value: '1',
            label: 'COMMON.ELECTRICITY_GAS_WATER',
        },
        {
            value: '2',
            label: 'COMMON.AGRICULTURE_FORESTRY_FISHING',
        },
        {
            value: '3',
            label: 'COMMON.MINING_QUARRYING',
        },
        {
            value: '4',
            label: 'COMMON.MANUFACTURING',
        },
        {
            value: '5',
            label: 'COMMON.TRANSPORT_STORAGE_COMMUNICATION',
        },
        {
            value: '6',
            label: 'COMMON.FINANCING_INSURANCE_REAL_ESTATE_BUSINESS_SERVICE',
        },
        {
            value: '7',
            label: 'COMMON.CONSTRUCTION',
        },
        {
            value: '8',
            label: 'COMMON.WHOLESALE_RETAIL_TRADE_RESTAURANTS_HOTELS',
        },
        {
            value: '9',
            label: 'COMMON.OTHER_SERVICES',
        },
        {
            value: '10',
            label: 'COMMON.TOURISM',
        },
    ];
    PurposeOfFinancingOptions = [
        {
            value: 'CF',
            label: 'COMMON.CONTRACT_FINANCING',
        },
        {
            value: 'IF',
            label: 'COMMON.INVOICE_FINANCING',
        },
        {
            value: 'RC',
            label: 'COMMON.REVOLVING_CREDIT',
        },
        {
            value: 'TF',
            label: 'COMMON.TERM_FINANCING',
        },
        {
            value: 'WC',
            label: 'COMMON.WORKING_CAPITAL',
        },
    ];
    oneYearMsg;
    twoYearMsg;
    threeYearMsg;
    fourYearMsg;

    bizConst = { cCaseBizConst: null };

    activeTab: string = 'portfolio';
    aliasLabelMap: { [key: string]: string } = {
        BANK_STATEMENT: 'Bank Statement',
        PROOF_OF_ADDRESS: 'Proof of Address',
        SSM_DOC: 'SSM Certificate',
        CTOS: 'CTOS',
        CCRIS: 'CCRIS',
        NRIC: 'IC',
        CONSENT_DOC: 'Declaration Form',
    };

    constructor(
        public dialogRef: MatDialogRef<ApplicationDetailsComponent>,
        @Inject(MAT_DIALOG_DATA) public data: any,
        private translate: TranslateService,
        private applicationService: ApplicationService,
        private currencyPipe: CurrencyPipe,
        private pdfService: PdfService,
        private toastr: ToastrService
    ) {}

    ngOnInit() {
        this.getTranslations();
    }

    getTranslations() {
        this.translate
            .get('COMMON.A_YEAR_LESS_THAN_TWO')
            .subscribe((data: any) => {
                this.oneYearMsg = data;
            });

        this.translate
            .get('COMMON.TWO_LESS_THAN_THREE')
            .subscribe((data: any) => {
                this.twoYearMsg = data;
            });

        this.translate
            .get('COMMON.THREE_LESS_THAN_FOUR')
            .subscribe((data: any) => {
                this.threeYearMsg = data;
            });

        this.translate.get('COMMON.FOUR_AND_ABOVE').subscribe((data: any) => {
            this.fourYearMsg = data;
        });
    }

    getBizRegOrNric(data: any): string {
        return data?.cCaseBizConst !== 'IND'
            ? data?.cCaseBizRegNo
            : data?.cCaseNric;
    }

    getBusinessConstitution(bizconst: string) {
        return this.businessTypeOptions.find((item) => item.value === bizconst)
            .label;
    }

    getBusinessConstitutionValue(code: string | null | undefined): string {
        if (!code) {
            return 'N/A';
        }

        const key = this.getBusinessConstitution(code);
        return this.translate.instant(key);
    }

    getBizName(data: any): string {
        return data === null || data === undefined || data === ''
            ? 'N/A'
            : data;
    }

    getBumiputeraStatus(value: any): string {
        if (value === null || value === undefined) {
            return 'N/A';
        }

        if (value === 'Y') {
            return 'COMMON.YES';
        }

        if (value === 'N') {
            return 'COMMON.NO';
        }

        return 'N/A';
    }

    getTranslatedBumiputeraStatus(value: string | null): string {
        const key = this.getBumiputeraStatus(value); // returns 'COMMON.YES', 'COMMON.NO', or 'N/A'
        return key === 'N/A' ? 'N/A' : this.translate.instant(key);
    }

    getSabahSarawakRegistered(value: any): any {
        if (value == null) {
            return 'N/A';
        } else {
            return value === 'Y' ? 'COMMON.YES' : 'COMMON.NO';
        }
    }

    getDisplayLabel<T extends { value: string; label: string }>(
        options: T[],
        key: string | null | undefined
    ): string {
        if (!key) {
            return 'N/A';
        }

        const found = options.find((item) => item.value === key);
        return found ? found.label : 'N/A';
    }

    getPurposeOfFinancing(bizPof: string | null | undefined): string {
        return this.getDisplayLabel(this.PurposeOfFinancingOptions, bizPof);
    }

    getTranslatedPurposeOfFinancing(bizPof: string | null | undefined): string {
        const key = this.getPurposeOfFinancing(bizPof);
        return key === 'N/A' ? 'N/A' : this.translate.instant(key);
    }

    getBusinessSector(bizSector: string | null | undefined): string {
        const key = this.getDisplayLabel(
            this.businessIndustryOptions,
            bizSector
        );
        return key === 'N/A' ? 'N/A' : this.translate.instant(key);
    }

    getFormattedCurrency(
        value: number | null | undefined,
        currencyCode: string = 'RM',
        display: 'code' | 'symbol' | 'symbol-narrow' = 'symbol',
        digitsInfo: string = '1.0-0'
    ): string {
        if (value === null || value === undefined) return 'N/A';
        return (
            this.currencyPipe.transform(
                value,
                currencyCode,
                display,
                digitsInfo
            ) || 'N/A'
        );
    }

    getBizProfitLabel(data: any): string {
        if (
            data.cCaseBizProfit == null &&
            (data.cCaseBizOpYear === 0.5 || data.cCaseBizOpYear === 0.6)
        ) {
            return 'N/A';
        }
        if (data.cCaseBizProfit == null && data.cCaseBizOpYear >= 1) {
            return 'NON_PROFITABLE';
        }
        if (data.cCaseBizProfit?.split('')[1] === '0') {
            return 'NON_PROFITABLE';
        }

        const firstChar = data.cCaseBizProfit?.[0];
        switch (firstChar) {
            case '1':
                return 'LATEST_1_YEAR';
            case '2':
                return 'LATEST_2_YEARS';
            case '3':
                return 'LATEST_3_YEARS';
            default:
                return '';
        }
    }

    getTranslatedBizProfitLabel(data: any): string {
        const key = this.getBizProfitLabel(data);
        return key === 'N/A' ? 'N/A' : this.translate.instant(key);
    }

    getBizOperationHeader(c: any): string {
        if (c.cCaseBizOpYear >= 1) {
            return 'COMMON.YEARS_IN_OPERATION';
        }
        if (c.cCaseBizProfit == null && c.cCaseBizOpYear < 1) {
            return 'COMMON.MONTHS_IN_OPERATION';
        }
        return '';
    }

    getBizOperationLabel(c: any): string {
        if (c.cCaseBizProfit == null && c.cCaseBizOpYear < 1) {
            if (c.cCaseBizOpYear === 0.5) {
                return 'LESS_THAN_6_MONTHS';
            }
            if (c.cCaseBizOpYear === 0.6) {
                return '6_MONTHS_LESS_THAN_1_YEAR';
            }
            return 'COMMON.MONTHS_IN_OPERATION';
        }

        if (c.cCaseBizOpYear >= 1) {
            return this.getProbabilityTrend(c.cCaseBizOpYear); // keep your existing logic here
        }

        return '';
    }

    getProbabilityTrend(val: string): string {
        if (val == '1') {
            return this.oneYearMsg;
        } else if (val == '2') {
            return this.twoYearMsg;
        } else if (val == '3') {
            return this.threeYearMsg;
        } else if (val == '4') {
            return this.fourYearMsg;
        }
    }

    getDocInfo(alias: string) {
        return this.data.documents.find((d) => d.alias === alias);
    }

    viewORdownloadDocs(docId: any) {
        let data = {
            caseId: this.data.snsID,
            docsId: docId,
        };

        this.applicationService.viewDocument(data).subscribe({
            next: (res: any) => {
                let docLink = res?.URL;
                window.open(docLink, '_blank');
            },
            error: (err: any) => {
                this.toastr.error(err.error.message);
            },
        });
    }

    downloadAsPdf(data) {
        // Create the reportData structure that matches the React version
        const reportData = {
            pcaseloan: data,
        };

        // Extract caseId from the data
        const caseId = data.caseId || data.snsID || 'unknown';
        this.pdfService.generatePdf(reportData, caseId);
    }

    closeDialog(): void {
        this.dialogRef.close({ dialogClosed: true });
    }
}
